import { JWTPayload } from 'jose';
import { extractToken, getCookie } from './modules/cookie';

/**
 * A quick note on the types used in this file:
 */

/**
 * The id token is a JWT token that contains information about the user.
 */
export interface IdToken extends JWTPayload {
  isInFirstTimeLoginBrand: boolean;
  'https://mediahuis.com/principal-type': 'USER';
  'https://mediahuis.com/subscribed-accesses': Array<string>;
  given_name: string;
  family_name: string;
  nickname: string;
  name: string;
  picture: string;
  gender: {
    type: 'Male' | 'Female' | 'Other';
  };
  updated_at: string;
  email: string;
  email_verified: boolean;
}

/**
 * The access token is a JWT token that contains access information about the user.
 */
export interface AccessToken extends JWTPayload {
  'https://mediahuis.com/principal-type': 'USER';
  'https://mediahuis.com/subscribed-accesses': Array<string>;
  iss: string;
  sub: string;
  aud: Array<string>;
  iat: number;
  exp: number;
  azp: string;
  scope: string;
}

/**
 * @private
 * @param clientId Auth0 client id
 * @returns URLSearchParams
 */
export function getConfig(clientId: string) {
  return new URLSearchParams(getCookie(`auth0_${clientId}_config`));
}

/**
 * Get the access token of a logged in user. Useful when you need to send this token to a backend service from the client.
 *
 * @param clientId Auth0 client id
 * @returns Access token or null
 */
export function getAccessToken(clientId: string) {
  return getCookie(`auth0_${clientId}_acc_token`);
}

/**
 * Returns the access token fully parsed and decoded of a loggedin user.
 *
 * @param clientId Auth0 client id
 * @returns The parsed access token or null
 */
export async function getUserSubscriptions(clientId: string) {
  const decodedAccessToken = await getDecodedAccessToken(clientId);

  return decodedAccessToken?.['https://mediahuis.com/subscribed-accesses'] ?? [];
}

/**
 * Returns the access token fully parsed and decoded of a loggedin user.
 *
 * @param clientId Auth0 client id
 * @returns The parsed access token or null
 */
export async function getDecodedAccessToken(clientId: string) {
  return extractToken(`auth0_${clientId}_acc_token`) as AccessToken;
}

/**
 * Retrieve the raw id token of a logged in user. If you're looking to extract this infomration you can simply use the `getUserInfo` function instead.
 *
 * @param clientId Auth0 client id
 * @returns Raw JWT id token or null
 */
export function getIdToken(clientId: string) {
  return getCookie(`auth0_${clientId}_id_token`);
}

/**
 * Returns the id token fully parsed and decoded of a loggedin user.
 *
 * @param clientId Auth0 client id
 * @returns The parsed id token or null
 */
export async function getUserInfo(clientId: string): Promise<IdToken> {
  return extractToken(`auth0_${clientId}_id_token`) as IdToken;
}

/**
 * Is the user authenticated?
 *
 * @param clientId Auth0 client id
 * @returns True if the user is authenticated, false otherwise
 */
export function isAuthenticated(clientId: string): boolean {
  return !!getAccessToken(clientId) && !!getIdToken(clientId);
}

/**
 * Is the email verified?
 *
 * @param clientId Auth0 client id
 * @returns True if the email is verified, false otherwise
 */
export async function isEmailVerified(clientId: string): Promise<boolean> {
  const userInfo = await getUserInfo(clientId);

  if (!userInfo) {
    return false;
  }

  return userInfo.email_verified;
}

export async function sendEmailVerification(clientId: string): Promise<boolean> {
  const config = getConfig(clientId);
  const userInfo = await getUserInfo(clientId);
  if (userInfo.email_verified || !config.has('base') || !config.has('redirectUri')) {
    return false;
  }

  const searchParams = new URLSearchParams({
    client_id: clientId,
    response_type: 'token',
    audience: 'https://ciam-api.mediahuis.com',
    redirect_uri: `${window.location.origin}${config.get('redirectUri')}`,
    response_mode: 'web_message',
    prompt: 'none',
  });

  const url = new URL(`${config.get('issuer')}/authorize?${searchParams}`);
  const iframe = document.createElement('iframe');

  return new Promise<boolean>((resolve) => {
    window.addEventListener('message', async function onMessage(event) {
      if (event.data.type === 'authorization_response') {
        window.removeEventListener('message', onMessage);
        document.body.removeChild(iframe);

        const verifyEmailRequestURL = new URL(`${window.location.origin}${config.get('base')}/verify-email-request`);
        verifyEmailRequestURL.searchParams.append('account', userInfo.sub);
        const token = event.data.response.access_token;
        const resp = await this.fetch(verifyEmailRequestURL, {
          method: 'POST',
          credentials: 'omit',
          headers: {
            authorization: 'Bearer ' + token,
          },
          body: JSON.stringify({
            clientId,
          }),
        });

        resolve(resp.ok);
      }
    });
    iframe.src = url.toString();
    document.body.appendChild(iframe);
  });
}

/**
 * Some brands require to show an email confirmation before reading any metered or plus articles.
 * showEmailConfirmation let you put our shared component based on the Mediahuis design system in your page.
 *
 * @param options The list of options to pass to the popup
 * @param options.clientId Auth0 client id
 * @param options.type The type of email confirmation to show
 * @param options.el The DOM element to render the email confirmation in
 * @returns
 */
export async function showEmailConfirmation({
  type,
  brand,
  clientId,
  el,
}: {
  type: 'paywall';
  clientId: string;
  brand: string;
  el: HTMLElement;
}): Promise<void> {
  const { appendEmailVerificationWall } = await import('./modules/email-confirmation/EmailConfirmationWall');

  if (type !== 'paywall') {
    throw new Error(`Unsupported type: ${type}, only paywall is currently implemented.`);
  }

  if (!isAuthenticated(clientId)) {
    throw new Error(`User is not authenticated.`);
  }

  const userInfo = await getUserInfo(clientId);

  if (userInfo.email_verified) {
    throw new Error(`User has already been verified.`);
  }

  return appendEmailVerificationWall(el, { email: userInfo.email, brand, clientId });
}

/**
 * If you want to control when to show the silent login popup.
 *
 * Note that we're using the Mediahuis design system to create these popups.
 *
 * @param options The list of options to pass to the popup
 * @param options.clientId Auth0 client id
 * @param options.brand The full name of the brand (e.g. "De Standaard")
 * @returns
 */
export async function showSilentLoginNotification({ clientId, brand }: { clientId: string; brand: string }): Promise<void> {
  if (!window?.location?.search?.includes('_sl=1')) {
    return;
  }

  const parsedUrl = new URL(window.location.href);
  parsedUrl.searchParams.delete('_sl');
  window.history.replaceState(history.state, undefined, parsedUrl.toString());
  const userInfo = await getUserInfo(clientId);

  if (!userInfo.email) {
    return;
  }

  const { appendNotificationToast } = await import('./modules/silent-login/NotificationToast');

  let root = document.getElementById('auth0_silent-login-root') as HTMLElement;
  if (!root) {
    root = document.createElement('div');
    root.id = 'auth0_silent-login-root';
    root.className = 'chameleon-theme-mh';
    Object.assign(root.style, {
      position: 'absolute',
      top: 0,
      right: 0,
      zIndex: '9999',
    });
  }

  await appendNotificationToast(root, { email: userInfo.email, brand });

  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      if (!document.getElementById('auth0_silent-login-root')) {
        document.body.appendChild(root);
      }

      resolve();
    });
  });
}
